import { computed, inject, Injectable } from '@angular/core';
import { BaseWorkflowStateService } from '../../base-workflow-state.service';
import { AdditionalOfferData, OfferType } from '../../../../../../../models/order/workflow/additional-offer.model';
import { VideoAdditionalOfferData } from '../../../../../../../models/order/workflow/video/video-additional-offer.model';
import { VideoFlowStateService } from '../video-flow-state.service';
import { VideoProductResponseService } from '../../../order-api-response/video-pd-response.service';
import { FeatureFlagService } from '../../../../../../../services/feature-flag.service';



@Injectable({ providedIn: 'root' })
export class AdditionalVideoOffersStateService extends BaseWorkflowStateService<VideoAdditionalOfferData> {
    private flowStateService = inject(VideoFlowStateService);
    private videoPdResponseService = inject(VideoProductResponseService);
    override stateKey: string = 'videoOffers';
    private featureFlagService = inject(FeatureFlagService);

    customizations = computed(() => this.videoPdResponseService.getCustomizations().filter(cust =>
        cust.customization.DetailedGroupName === 'OptionalIncludedOffers' ||
        cust.customization.DetailedGroupName === 'RequiredIncludedOffers' ||
        cust.customization.DetailedGroupName === 'AdditionalOffers' ||
        cust.customization.DetailedGroupName === 'LocalChannelOffer' ||
        cust.customization.DetailedGroupName === 'ProtectionPlanOffer' ||
        this.videoPdResponseService.isDTVSwitcherOffer(cust.customization) ||
        (cust.customization.SubGroupName === 'DTV-Promotions' && this.featureFlagService.isDTVCommercialBannerEnabled())
    ) || []);

    bannerCustomizations = computed(() => this.videoPdResponseService.getCustomizations().filter(cust => cust.customization.SubGroupName?.toLocaleLowerCase() === 'dtv-coming-soon-promotions' && this.featureFlagService.isDTVCommercialBannerEnabled()) || []);

    offers = computed(() => {
        return this.state()?.offers ?? [];
    });

    includedOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Required) || [];
    });

    additionalOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Additional) || [];
    });

    equipmentOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Equipment) || [];
    });

    findOfferByCustomizationAndChoice(customizationCode: string, choiceCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode && (offer.choice?.ChoiceCode === choiceCode || offer.choiceCode === choiceCode));
    }

    findOffersByCustomization(customizationCode: string): AdditionalOfferData[] {
        return this.offers().filter(offer => offer.customizationCode === customizationCode);
    }

    findDropdownSelectedOffer(customizationCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode);
    }

    addOffer(offer: AdditionalOfferData): void {
        const currentState = this.state();
        const existingOfferIndex = currentState?.offers?.findIndex(o => o.choiceCode === offer.choiceCode);
        if (existingOfferIndex > -1) {
            currentState.offers[existingOfferIndex] = offer;
        } else {
            currentState.offers.push(offer);
        }
        this.updateData(currentState);
    }

    removeOffer(choiceCode: string): void {
        const currentState = this.state();
        currentState.offers = currentState.offers?.filter(o => o.choiceCode !== choiceCode);
        this.updateData(currentState);
    }

    removeOffersByCustomization(customizationCode: string): void {
        const currentState = this.state();
        currentState.offers = currentState.offers?.filter(o => o.customizationCode !== customizationCode);
        this.updateData(currentState);
    }

    addABPoffer(abpEnabled: boolean): void {
        const abpOffer = this.videoPdResponseService.getABPResidentialStreamAdditionalOfferData(abpEnabled);
        this.addOffer(abpOffer);
        this.removeOffer(abpEnabled ? 'ATT-VIDEO-ABP-PAPERLESS-YES' : 'ATT-VIDEO-ABP-BOTH-YES');
    }

    addABPResidentialSatelliteOffer(abpEnabled: boolean): void {
        const abpOffer = this.videoPdResponseService.getABPResidentialSatelliteOfferData(abpEnabled);
        this.addOffer(abpOffer);
        this.removeOffer(abpEnabled ? 'ATT-DTV-ABP-NO' : 'ATT-DTV-ABP-YES');
    }

    addABPAutoBillPayOffer(abpEnabled: boolean): void {
        const { both, paperless } = this.videoPdResponseService.getAutoBillPayChoiceCodes();
        if (abpEnabled) {
            this.addOffer(this.videoPdResponseService.getABPAutoBillPayOfferData());
            if (paperless) this.removeOffer(paperless);
        } else {
            if (both) this.removeOffer(both);
            if (paperless) this.removeOffer(paperless);
        }
    }

    addDefaultAdditionalOffer(): boolean {
        let hasDefaultOffers = false;
        const allVideoOfferCustomizations = this.customizations();
        if (allVideoOfferCustomizations) {
            const selectedCustomizations = allVideoOfferCustomizations.filter(x => x.customization.Choices?.Choice?.some(choice => choice.DefaultSelected)) || [];
            const selectedChoices = selectedCustomizations.flatMap(cust => cust.customization.Choices.Choice.filter(choice => choice.DefaultSelected).map(choice => ({ choice, customization: cust.customization })));
            if (selectedChoices.length > 0) {
                selectedChoices.forEach(element => {
                    const offerData: AdditionalOfferData = {
                        choiceCode: element.choice.ChoiceCode,
                        choice: element.choice,
                        customizationCode: element.customization.CustomizationCode,
                        type: OfferType.Additional,
                        accepted: true,
                        usoc: element.choice.Usoc || ''
                    };
                    this.addOffer(offerData);
                    hasDefaultOffers = true;
                });
            }
        }
        return hasDefaultOffers;
    }


    protected override saveLocalDraft(): void {
        this.flowStateService.updateData({ [this.stateKey]: this.state() });
    }

    resetData() {
        this.state.set(null);
        this.saveLocalDraft();
    }
}
